import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { createUser, updateUser, deleteUser, getAdminUsers } from '../helper/Api/AdminApi';
import { toast } from "react-toastify";

const AdminUsersListLayer = () => {
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [formData, setFormData] = useState({
        fullName: '',
        contactNumber: '',
        email: '',
        dateOfBirth: '',
        username: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [showTooltip, setShowTooltip] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [5, 10, 25, 50, 'All'];

    const totalItems = users.length;
    const totalPages = Math.ceil(totalItems / (pageSize === 'All' ? totalItems : pageSize));

    const startIndex = pageSize === 'All' ? 0 : (currentPage - 1) * pageSize;
    const endIndex = pageSize === 'All' ? totalItems : currentPage * pageSize;
    
    const paginatedUsers = pageSize === 'All' 
        ? users 
        : users.slice(startIndex, endIndex);

    const getPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) pages.push(i);
        return pages;
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePageChange = (page) => setCurrentPage(page);

    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            try {
                const fetchedUsers = await getAdminUsers();
                setUsers(fetchedUsers);
                console.log('fetchedUsers', fetchedUsers);
                // toast.success('Users fetched successfully!');
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to fetch users');
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, []);

    useEffect(() => {
        if (pageSize === 'All') {
            setCurrentPage(1);
        }
    }, [pageSize]);

    useEffect(() => {
        if (showModal || showConfirm) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showModal, showConfirm]);

    const validateForm = () => {
        const newErrors = {};
        const cleanNumber = formData.contactNumber.replace(/[\s()-]/g, '');
        const phoneRegex = /^\d{10}$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.fullName) newErrors.fullName = 'Please enter Full Name';
        else if (formData.fullName.length > 100) newErrors.fullName = 'Full Name max 100 characters';

        if (!cleanNumber) newErrors.contactNumber = 'Please enter Contact Number';
        else if (!phoneRegex.test(cleanNumber)) {
            newErrors.contactNumber = 'Invalid Contact Number format';
        }

        if (!formData.email) newErrors.email = 'Please enter Email';
        else if (!emailRegex.test(formData.email)) newErrors.email = 'Invalid Email format';

        if (!formData.username) newErrors.username = 'Please enter Username';
        else if (formData.username.length < 8) newErrors.username = 'Username must be at least 8 characters';

        if (!currentUser && !formData.password)
            newErrors.password = 'Please enter Password';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        let { name, value } = e.target;

        if (name === 'contactNumber') {
            // Allow only numbers and limit to 10 digits
            value = value.replace(/[^0-9]/g, '');
            if (value.length > 10) {
                value = value.slice(0, 10);
            }

            // Validate on change
            if (!value) {
                setErrors(prev => ({ ...prev, contactNumber: 'Please enter Contact Number' }));
            } else if (value.length !== 10) {
                setErrors(prev => ({ ...prev, contactNumber: 'Invalid Contact Number format' }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.contactNumber;
                    return newErrors;
                });
            }
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const payload = {
                FullName: formData.fullName,
                ContactNumber: formData.contactNumber,
                EMailID: formData.email,
                DOB: formData.dateOfBirth,
                Username: formData.username,
                Password: formData.password || undefined,
                EmailAlert: true,
                PushAlert: false
            };

            if (currentUser) {
                await updateUser(currentUser.id, payload);
                toast.success("User updated successfully!");
            } else {
                await createUser(payload);
                toast.success("User created successfully!");
            }

            const updatedUsers = await getAdminUsers();
            setUsers(updatedUsers);

            setShowModal(false);
            setFormData({ fullName: '', contactNumber: '', email: '', dateOfBirth: '', username: '', password: '' });
            setCurrentUser(null);
            setErrors({});
        } catch (error) {
            toast.error(error.response?.data?.Message || "Failed to save user");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user) => {
        setCurrentUser(user);
        setFormData({
            fullName: user.fullName,
            contactNumber: user.contactNumber,
            email: user.email,
            dateOfBirth: user.dateOfBirth,
            username: user.username,
            password: user.password
        });
        setErrors({});
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            const response = await deleteUser(id);
            setUsers(users.filter(user => user.id !== id));
            toast.success(response.message || "User deleted successfully!");
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to delete user");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                    
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-sm text-secondary-light">Show</span>
                        <div className="dropdown">
                            <button 
                                className="btn btn-outline-secondary btn-sm dropdown-toggle py-2 px-3 radius-6"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                {pageSize === 'All' ? 'All' : pageSize}
                            </button>
                            <ul className="dropdown-menu shadow-sm border-0 radius-8 py-2">
                                {pageSizeOptions.map((option) => (
                                    <li key={option}>
                                        <button
                                            className={`dropdown-item py-2 px-3 text-sm ${pageSize === option ? 'active bg-primary-50 text-primary-600' : ''}`}
                                            onClick={() => handlePageSizeChange(option)}
                                        >
                                            {option}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                <button
                    className="btn btn-primary btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                    onClick={() => {
                        setCurrentUser(null);
                        setFormData({ fullName: '', contactNumber: '', email: '', dateOfBirth: '', username: '', password: '' });
                        setErrors({});
                        setShowModal(true);
                    }}
                    disabled={loading}
                >
                    <Icon icon="ic:baseline-plus" className="icon text-xl" />
                    Add New User
                </button>
            </div>

            <div className="card-body p-24">
                {loading ? (
                    <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading Users...</p>
          </div>
                ) : (
                    <>
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th className="text-center">Action</th>
                                        <th>Full Name</th>
                                        <th>Email</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedUsers.map((user) => (
                                        <tr key={user.id}>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-10">
                                                    <button
                                                        className="bg-primary-50 text-primary-600 w-32-px h-32-px rounded-circle"
                                                        onClick={() => handleEdit(user)}
                                                    >
                                                        <Icon icon="lucide:edit" className="icon text-lg" />
                                                    </button>
                                                    <button
                                                        className="bg-danger-50 text-danger-600 w-32-px h-32-px rounded-circle"
                                                        onClick={() => {
                                                            setDeleteId(user.id);
                                                            setShowConfirm(true);
                                                        }}
                                                    >
                                                        <Icon icon="fluent:delete-24-regular" className="icon text-lg" />
                                                    </button>
                                                </div>
                                            </td>
                                            <td>{user.fullName}</td>
                                            <td>{user.email}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                            <div className="d-flex align-items-center gap-3">
                                <span className="text-secondary-light text-sm">
                                    Showing {pageSize === 'All' ? 1 : Math.min(startIndex + 1, totalItems)} to{" "}
                                    {Math.min(endIndex, totalItems)} of {totalItems} entries
                                </span>
                            </div>

                            {pageSize !== 'All' && totalPages > 1 && (
                                <ul className="pagination d-flex flex-wrap align-items-center gap-2 m-0 p-0">
                                    <li className="page-item">
                                        <button
                                            className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                                            onClick={handlePreviousPage}
                                            disabled={currentPage === 1}
                                            style={{ opacity: currentPage === 1 ? 0.4 : 1 }}
                                        >
                                            <Icon icon="ep:d-arrow-left" className="text-lg" />
                                        </button>
                                    </li>

                                    {getPageNumbers().map((page) => (
                                        <li className="page-item" key={page}>
                                            <button
                                                className={`page-link radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px fw-medium text-sm transition-all ${currentPage === page
                                                    ? "bg-primary-600 text-white shadow-sm"
                                                    : "bg-neutral-100 text-secondary-light hover:bg-neutral-200"
                                                    }`}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </button>
                                        </li>
                                    ))}

                                    <li className="page-item">
                                        <button
                                            className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                                            onClick={handleNextPage}
                                            disabled={currentPage === totalPages}
                                            style={{ opacity: currentPage === totalPages ? 0.4 : 1 }}
                                        >
                                            <Icon icon="ep:d-arrow-right" className="text-lg" />
                                        </button>
                                    </li>
                                </ul>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Confirmation Modal */}
            {showConfirm && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.15)" }}>
                    <div className="modal-dialog modal-sm modal-dialog-centered">
                        <div className="modal-content border-0 radius-12 shadow-lg p-24" style={{ background: "#fff" }}>
                            <h6 className="text-center fw-semibold mb-12">Are you sure?</h6>
                            <p className="text-center text-secondary-light mb-20" style={{ fontSize: "14px" }}>
                                This action cannot be undone.
                            </p>
                            <div className="d-flex justify-content-center gap-2">
                                <button
                                    className="btn btn-light px-20 py-6 radius-8"
                                    onClick={() => setShowConfirm(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-danger px-20 py-6 radius-8"
                                    onClick={() => {
                                        setShowConfirm(false);
                                        handleDelete(deleteId);
                                    }}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content border shadow-lg radius-12">
                            <div className="modal-header">
                                <h4 className="modal-title">{currentUser ? "Edit User" : "Add New User"}</h4>
                                <button className="btn-close" onClick={() => { setShowModal(false); setErrors({}); }} />
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Full Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleInputChange}
                                        />
                                        {errors.fullName && <span className="text-danger">{errors.fullName}</span>}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Contact Number *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="contactNumber"
                                            value={formData.contactNumber}
                                            onChange={handleInputChange}
                                        />
                                        {errors.contactNumber && <span className="text-danger">{errors.contactNumber}</span>}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Email *</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                        />
                                        {errors.email && <span className="text-danger">{errors.email}</span>}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Username *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleInputChange}
                                        />
                                        {errors.username && <span className="text-danger">{errors.username}</span>}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Date of Birth *</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="dateOfBirth"
                                            value={formData.dateOfBirth}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3 position-relative">
                                        <label className="form-label">{currentUser ? "New Password" : "Password"} *</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                        />
                                        {errors.password && <span className="text-danger">{errors.password}</span>}
                                    </div>
                                </div>
                                <div className="text-center mt-4">
                                    <button className="btn btn-primary me-3" onClick={handleSubmit}>
                                        {currentUser ? "Update" : "Add"}
                                    </button>
                                    <button className="btn btn-secondary" onClick={() => { setShowModal(false); setErrors({}); }}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsersListLayer;